// SPDX-License-Identifier: AGPL-3.0-or-later

import {ConfirmModal} from '@app/features/app/components/dialogs/ConfirmModal';
import {DESKTOP_DOWNLOAD_URL, PRODUCT_NAME} from '@app/features/app/config/I18nDisplayConstants';
import NewDeviceMonitoring from '@app/features/auth/state/NewDeviceMonitoring';
import {KeybindRecorder} from '@app/features/input/components/KeybindRecorder';
import Keybind, {getDefaultKeybind} from '@app/features/input/state/Keybind';
import {Accordion} from '@app/features/ui/accordion/Accordion';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import {Select, type SelectOption} from '@app/features/ui/components/form/Select';
import {Switch} from '@app/features/ui/components/form/Switch';
import {Slider} from '@app/features/ui/components/Slider';
import {RadioGroup, type RadioOption} from '@app/features/ui/radio_group/RadioGroup';
import {getElectronAPI, openExternalUrl} from '@app/features/ui/utils/NativeUtils';
import {WarningAlert} from '@app/features/ui/warning_alert/WarningAlert';
import {EntranceSoundSection} from '@app/features/user/components/modals/tabs/components/EntranceSoundSection';
import {MicTestSection} from '@app/features/user/components/modals/tabs/components/MicTestSection';
import {useMediaPermission} from '@app/features/user/components/modals/tabs/hooks/useMediaPermission';
import styles from '@app/features/user/components/modals/tabs/VoiceTab.module.css';
import * as VoiceSettingsCommands from '@app/features/voice/commands/VoiceSettingsCommands';
import {InputMonitoringCTAModal} from '@app/features/voice/components/modals/InputMonitoringCTAModal';
import MediaEngine from '@app/features/voice/engine/MediaEngineFacade';
import NativePermission from '@app/features/voice/state/NativePermission';
import type VoiceSettings from '@app/features/voice/state/VoiceSettings';
import {
	type ExternalAudioProcessorMatch,
	findExternalProcessorForDevice,
} from '@app/features/voice/utils/ExternalAudioProcessor';
import {hasDeviceLabels, resolveEffectiveDeviceId} from '@app/features/voice/utils/VoiceDeviceManager';
import type {VoiceProcessingMode} from '@app/features/voice/utils/VoiceProcessingProfile';
import {VOICE_VOLUME_MAX_PERCENT} from '@app/features/voice/utils/VoiceVolumeUtils';
import {Trans, useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useEffect, useMemo, useState} from 'react';

type NoiseSuppressionMethod = 'enhanced' | 'standard' | 'none';
interface VoiceTabProps {
	voiceSettings: typeof VoiceSettings;
	hasPremium: boolean;
	autoRequestPermission?: boolean;
}
function resolveNoiseSuppressionMethod(deepFilterEnabled: boolean, browserNsEnabled: boolean): NoiseSuppressionMethod {
	if (deepFilterEnabled) return 'enhanced';
	if (browserNsEnabled) return 'standard';
	return 'none';
}
export const VoiceTab: React.FC<VoiceTabProps> = observer(({voiceSettings, autoRequestPermission = true}) => {
	const {t, i18n} = useLingui();
	const {
		inputDeviceId,
		outputDeviceId,
		inputVolume,
		outputVolume,
		echoCancellation,
		noiseSuppression,
		autoGainControl,
		deepFilterNoiseSuppression,
		vadThreshold,
		vadAutoSensitivity,
	} = voiceSettings;
	const {
		devices,
		status: permissionStatus,
		requestPermission,
	} = useMediaPermission('audio', {
		autoRequest: autoRequestPermission,
	});
	const isNativeDesktop = NativePermission.isDesktop;
	const isNativeMac = NativePermission.isMacOS;
	const inputMonitoringGranted = NativePermission.isInputMonitoringGranted;
	const [isWayland, setIsWayland] = useState(false);
	const [waylandCapabilities, setWaylandCapabilities] = useState<{
		portalAvailable: boolean;
		evdevAvailable: boolean;
	} | null>(null);
	useEffect(() => {
		const electronApi = getElectronAPI() as Record<string, unknown> | null;
		if (electronApi == null) return;
		if (typeof electronApi.isWaylandSession === 'function') {
			void (electronApi.isWaylandSession as () => Promise<boolean>)().then(setIsWayland);
		}
		if (typeof electronApi.getWaylandCapabilities === 'function') {
			void (
				electronApi.getWaylandCapabilities as () => Promise<{
					isWayland: boolean;
					portalAvailable: boolean;
					evdevAvailable: boolean;
				} | null>
			)()?.then((caps) => {
				if (caps?.isWayland) {
					setWaylandCapabilities({portalAvailable: caps.portalAvailable, evdevAvailable: caps.evdevAvailable});
				}
			});
		}
	}, []);
	const inputDevices = useMemo(() => devices.filter((d) => d.kind === 'audioinput'), [devices]);
	const outputDevices = useMemo(() => devices.filter((d) => d.kind === 'audiooutput'), [devices]);
	const transmitMode = Keybind.transmitMode;
	const isPushToTalk = transmitMode === 'voice_push_to_talk';
	const pttPrimary = Keybind.getPrimaryCustomKeybind('voice_push_to_talk');
	const pttCombo = pttPrimary?.combo ?? {key: '', enabled: true, global: true};
	const pttReleaseDelay = Keybind.pushToTalkReleaseDelay;
	const isWaylandLimited =
		isWayland &&
		waylandCapabilities != null &&
		!waylandCapabilities.portalAvailable &&
		!waylandCapabilities.evdevAvailable;
	const isPttLimited = !isNativeDesktop || (isNativeMac && !inputMonitoringGranted) || isWaylandLimited;
	const defaultPttCombo = getDefaultKeybind('voice_push_to_talk', i18n);
	const inputHasLabels = hasDeviceLabels(inputDevices);
	const outputHasLabels = hasDeviceLabels(outputDevices);
	const effectiveInputDeviceId = resolveEffectiveDeviceId(inputDeviceId, inputDevices) ?? 'default';
	const effectiveOutputDeviceId = resolveEffectiveDeviceId(outputDeviceId, outputDevices) ?? 'default';
	const activeInputDevice = inputDevices.find((d) => d.deviceId === effectiveInputDeviceId) ?? null;
	const activeInputLabel = activeInputDevice?.label || null;
	const hasDeviceOverride = voiceSettings.hasVoiceProcessingModeOverrideForDeviceLabel(activeInputLabel);
	const voiceProcessingMode = voiceSettings.getVoiceProcessingModeForDeviceLabel(activeInputLabel);
	const externalProcessor: ExternalAudioProcessorMatch | null = inputHasLabels
		? findExternalProcessorForDevice(effectiveInputDeviceId, inputDevices)
		: null;
	const showExternalProcessorHint = externalProcessor !== null && voiceProcessingMode !== 'studio';
	const handleInputDeviceChange = (value: string) => {
		VoiceSettingsCommands.update({inputDeviceId: value});
	};
	const handleVoiceProcessingModeChange = (mode: VoiceProcessingMode) => {
		if (activeInputLabel) {
			VoiceSettingsCommands.setVoiceProcessingModeForDeviceLabel(activeInputLabel, mode);
		} else {
			VoiceSettingsCommands.update({voiceProcessingMode: mode});
		}
	};
	const handleClearDeviceOverride = () => {
		if (activeInputLabel) {
			VoiceSettingsCommands.clearVoiceProcessingModeForDeviceLabel(activeInputLabel);
		}
	};
	const inputDeviceOptions =
		inputDevices.length > 0 && inputHasLabels
			? inputDevices.map((device) => {
					const shortDeviceId = device.deviceId.slice(0, 8);
					return {
						value: device.deviceId,
						label: device.label || t`Microphone ${shortDeviceId}`,
					};
				})
			: [{value: 'default', label: t`Default`}];
	const outputDeviceOptions =
		outputDevices.length > 0 && outputHasLabels
			? outputDevices.map((device) => {
					const shortDeviceId = device.deviceId.slice(0, 8);
					return {
						value: device.deviceId,
						label: device.label || t`Speaker ${shortDeviceId}`,
					};
				})
			: [{value: 'default', label: t`Default`}];
	const profileOptions: Array<RadioOption<VoiceProcessingMode>> = [
		{
			value: 'voice',
			name: t`Focused Voice`,
			desc: t`Recommended. Tunes your mic for clear speech with stronger cleanup, echo control and a speech-first signal path.`,
		},
		{
			value: 'studio',
			name: t`Direct Input`,
			desc: t`Sends the raw, unprocessed audio from your input device. Ideal if you're already handling EQ, gates, routing or DSP elsewhere.`,
		},
		{
			value: 'custom',
			name: t`Custom`,
			desc: t`Tune the processing yourself: noise suppression, echo cancellation, gain control, and the mic activity guide below.`,
		},
	];
	const noiseSuppressionMethod = resolveNoiseSuppressionMethod(deepFilterNoiseSuppression, noiseSuppression);
	const noiseSuppressionOptions: Array<SelectOption<NoiseSuppressionMethod>> = [
		{value: 'enhanced', label: t`Enhanced (DeepFilterNet3)`},
		{value: 'standard', label: t`Standard (browser built-in)`},
		{value: 'none', label: t`None`},
	];
	const setNoiseSuppressionMethod = (method: NoiseSuppressionMethod) => {
		switch (method) {
			case 'enhanced':
				VoiceSettingsCommands.update({deepFilterNoiseSuppression: true, noiseSuppression: false});
				return;
			case 'standard':
				VoiceSettingsCommands.update({deepFilterNoiseSuppression: false, noiseSuppression: true});
				return;
			case 'none':
				VoiceSettingsCommands.update({deepFilterNoiseSuppression: false, noiseSuppression: false});
				return;
		}
	};
	const setPushToTalkEnabled = (enabled: boolean) => {
		const mode = enabled ? 'voice_push_to_talk' : 'voice_activity';
		if (enabled && !isNativeDesktop) {
			ModalCommands.push(
				modal(() => (
					<ConfirmModal
						title={t`Push-to-Talk (Limited)`}
						description={
							<p>
								<Trans>
									In your browser, Push-to-Talk only works while the Fluxer tab is focused. For system-wide Push-to-Talk
									that keeps working while you game or switch apps, install the desktop app.
								</Trans>
							</p>
						}
						primaryText={t`Download Desktop App`}
						primaryVariant="primary"
						secondaryText={t`I Understand`}
						onPrimary={() => {
							void openExternalUrl(DESKTOP_DOWNLOAD_URL);
						}}
						onSecondary={() => {
							Keybind.setTransmitMode(mode);
							MediaEngine.handlePushToTalkModeChange();
						}}
					/>
				)),
			);
			return;
		}
		if (enabled && isNativeMac && !inputMonitoringGranted) {
			ModalCommands.push(
				modal(() => (
					<InputMonitoringCTAModal
						onComplete={() => {
							void NativePermission.recheckInputMonitoring();
							Keybind.setTransmitMode(mode);
							MediaEngine.handlePushToTalkModeChange();
						}}
					/>
				)),
			);
			return;
		}
		Keybind.setTransmitMode(mode);
		MediaEngine.handlePushToTalkModeChange();
	};
	const handleOpenInputMonitoringModal = () => {
		ModalCommands.push(
			modal(() => (
				<InputMonitoringCTAModal
					onComplete={() => {
						void NativePermission.recheckInputMonitoring();
					}}
				/>
			)),
		);
	};
	const pttAvailable = voiceProcessingMode !== 'studio';
	const showPttDetails = pttAvailable && isPushToTalk;
	const renderPttControls = () => (
		<>
			<Switch
				label={<Trans>Use Push-to-Talk</Trans>}
				description={
					<Trans>
						Only transmit while you're holding down your chosen key or button. Turn this off to leave your mic live
						without a hold key.
					</Trans>
				}
				value={isPushToTalk}
				onChange={setPushToTalkEnabled}
				ariaLabel={t`Use Push-to-Talk`}
			/>
			{showPttDetails && (
				<div className={styles.pttSettings}>
					<div className={styles.pttSettingRow}>
						<div className={styles.pttSettingLabel}>
							<Trans>Shortcut</Trans>
						</div>
						<div className={styles.pttKeybindRow}>
							<KeybindRecorder
								action="voice_push_to_talk"
								value={pttCombo}
								defaultValue={defaultPttCombo}
								onChange={(combo) => {
									Keybind.setPrimaryCustomKeybindCombo('voice_push_to_talk', {
										...combo,
										global: pttCombo.global ?? true,
									});
								}}
								onReset={() => {
									if (defaultPttCombo) {
										Keybind.setPrimaryCustomKeybindCombo('voice_push_to_talk', {
											...defaultPttCombo,
											global: pttCombo.global ?? true,
										});
									}
								}}
							/>
						</div>
					</div>
					<div className={styles.pttSettingRow}>
						<div className={styles.pttSettingLabel}>
							<Trans>Release Delay</Trans>
						</div>
						<p className={styles.pttSettingDescription}>
							<Trans>
								How long to keep transmitting after you let go of the key. Helps avoid chopping off trailing words.
							</Trans>
						</p>
						<Slider
							defaultValue={pttReleaseDelay}
							factoryDefaultValue={20}
							minValue={20}
							maxValue={2000}
							step={10}
							markers={[20, 500, 1000, 1500, 2000]}
							stickToMarkers={false}
							onMarkerRender={(value) => `${value}ms`}
							onValueRender={(value) => `${value}ms`}
							onValueChange={(value) => Keybind.setPushToTalkReleaseDelay(value)}
							showResetButton={true}
						/>
					</div>
				</div>
			)}
			{isPushToTalk && isPttLimited && (
				<WarningAlert
					link={
						!isNativeDesktop
							? {
									label: <Trans>Download the desktop app for system-wide Push-to-Talk</Trans>,
									onClick: () => void openExternalUrl(DESKTOP_DOWNLOAD_URL),
								}
							: isWaylandLimited
								? undefined
								: {
										label: <Trans>Enable Input Monitoring permission</Trans>,
										onClick: handleOpenInputMonitoringModal,
									}
					}
				>
					{!isNativeDesktop ? (
						<Trans>Push-to-Talk will only work while the browser tab is focused.</Trans>
					) : isWaylandLimited ? (
						<Trans>
							Push-to-Talk on Wayland needs the 'input' group. Run: sudo usermod -aG input $USER, then log out and back
							in.
						</Trans>
					) : (
						<Trans>
							Push-to-Talk only works while Fluxer has focus until you grant Input Monitoring permission. Enable it to
							use any key or mouse button system-wide.
						</Trans>
					)}
				</WarningAlert>
			)}
		</>
	);
	const renderCustomProfile = () => (
		<div className={styles.profileSubSection}>
			{renderPttControls()}
			<Switch
				label={<Trans>Auto-Set Activity Threshold</Trans>}
				description={
					<Trans>
						Fluxer positions the activity marker in the mic test below for you. Turn this off if you want to place it
						manually.
					</Trans>
				}
				value={vadAutoSensitivity}
				onChange={(value) => VoiceSettingsCommands.update({vadAutoSensitivity: value})}
				ariaLabel={t`Auto-Set Activity Threshold`}
			/>
			{!vadAutoSensitivity && (
				<div className={styles.sensitivitySliderWrapper}>
					<div className={styles.sliderLabel}>
						<Trans>Activity Threshold</Trans>
					</div>
					<Slider
						value={vadThreshold}
						defaultValue={vadThreshold}
						factoryDefaultValue={50}
						minValue={0}
						maxValue={100}
						step={1}
						onValueRender={(value) => `${Math.round(value)}%`}
						asValueChanges={(value) => VoiceSettingsCommands.update({vadThreshold: value})}
						onValueChange={(value) => VoiceSettingsCommands.update({vadThreshold: value})}
						showResetButton={true}
					/>
				</div>
			)}
			<div>
				<div className={styles.sliderLabel}>
					<Trans>Noise Suppression</Trans>
				</div>
				<p className={styles.audioProcessingDescription}>
					<Trans>
						Enhanced uses a local neural filter for the cleanest results. Standard uses your browser's built-in
						processor. None sends everything untouched.
					</Trans>
				</p>
				<Select
					label={t`Noise Suppression`}
					value={noiseSuppressionMethod}
					options={noiseSuppressionOptions}
					onChange={setNoiseSuppressionMethod}
				/>
			</div>
			<Switch
				label={<Trans>Echo Cancellation</Trans>}
				description={<Trans>Stops your speakers from bleeding back into your microphone and looping.</Trans>}
				value={echoCancellation}
				onChange={(value) => VoiceSettingsCommands.update({echoCancellation: value})}
				ariaLabel={t`Echo Cancellation`}
			/>
			<Accordion id="voice-advanced" title={t`Advanced`}>
				<div className={styles.audioProcessingOptions}>
					<Switch
						label={<Trans>Automatic Gain Control</Trans>}
						description={
							<Trans>
								Evens out your mic volume so you stay at a consistent level. Forced off while Enhanced noise suppression
								is active because the two fight each other and can cause sudden loud bursts.
							</Trans>
						}
						value={autoGainControl}
						disabled={deepFilterNoiseSuppression}
						onChange={(value) => VoiceSettingsCommands.update({autoGainControl: value})}
						ariaLabel={t`Automatic Gain Control`}
					/>
				</div>
			</Accordion>
		</div>
	);
	return (
		<>
			{devices.length === 0 && permissionStatus !== 'loading' && permissionStatus !== 'granted' ? (
				<div className={styles.deviceNotice}>
					<div className={styles.deviceNoticeText}>
						<div className={styles.deviceNoticeTitle}>
							<Trans>No microphones detected</Trans>
						</div>
						<p className={styles.deviceNoticeDescription}>
							{permissionStatus === 'denied' ? (
								isNativeDesktop ? (
									<Trans>
										Allow Fluxer to access your microphone in System Settings → Privacy &amp; Security → Microphone.
									</Trans>
								) : (
									<Trans>
										Allow {PRODUCT_NAME} to access your microphone. Check your browser address bar or settings to enable
										permissions.
									</Trans>
								)
							) : (
								<Trans>{PRODUCT_NAME} needs access to list the available microphones and speakers.</Trans>
							)}
						</p>
					</div>
					<Button
						variant="secondary"
						small={true}
						onClick={() => {
							void requestPermission();
						}}
					>
						<Trans>Allow Microphone</Trans>
					</Button>
				</div>
			) : null}
			<div>
				<Select
					label={t`Input Device`}
					value={effectiveInputDeviceId}
					options={inputDeviceOptions}
					onChange={handleInputDeviceChange}
				/>
			</div>
			<div>
				<Select
					label={t`Output Device`}
					value={effectiveOutputDeviceId}
					options={outputDeviceOptions}
					onChange={(value) => VoiceSettingsCommands.update({outputDeviceId: value})}
				/>
			</div>
			<Switch
				label={<Trans>Show New Device Alerts</Trans>}
				description={<Trans>Get prompted when new audio devices are connected</Trans>}
				value={!NewDeviceMonitoring.suppressAlerts}
				onChange={(value) => NewDeviceMonitoring.setSuppressAlerts(!value)}
			/>
			<div>
				<div className={styles.sliderLabel}>
					<Trans>Input Volume</Trans>
				</div>
				<Slider
					value={inputVolume}
					defaultValue={inputVolume}
					factoryDefaultValue={100}
					minValue={0}
					maxValue={VOICE_VOLUME_MAX_PERCENT}
					step={1}
					markers={[0, 50, 100, 150, 200]}
					stickToMarkers={false}
					onMarkerRender={(value) => `${value}%`}
					onValueRender={(value) => `${value}%`}
					onValueChange={(value) => VoiceSettingsCommands.update({inputVolume: value})}
					showResetButton={true}
				/>
			</div>
			<div>
				<div className={styles.sliderLabel}>
					<Trans>Output Volume</Trans>
				</div>
				<Slider
					value={outputVolume}
					defaultValue={outputVolume}
					factoryDefaultValue={100}
					minValue={0}
					maxValue={VOICE_VOLUME_MAX_PERCENT}
					step={1}
					markers={[0, 50, 100, 150, 200]}
					stickToMarkers={false}
					onMarkerRender={(value) => `${value}%`}
					onValueRender={(value) => `${value}%`}
					onValueChange={(value) => VoiceSettingsCommands.update({outputVolume: value})}
					showResetButton={true}
				/>
			</div>
			<div className={styles.audioProcessing}>
				<div className={styles.audioProcessingLabel}>
					<Trans>Input Profile</Trans>
				</div>
				{showExternalProcessorHint && externalProcessor != null && (
					<WarningAlert
						link={{
							label: <Trans>Switch to Direct Input</Trans>,
							onClick: () => VoiceSettingsCommands.update({voiceProcessingMode: 'studio'}),
						}}
					>
						<Trans>
							Looks like you're using {externalProcessor.name}. Stacking Fluxer's voice processing on top can cause
							pumping or distortion. Direct Input passes your already-cleaned audio through untouched.
						</Trans>
					</WarningAlert>
				)}
				<div className={styles.audioProcessingCard}>
					<p className={styles.audioProcessingDescription}>
						<Trans>
							Pick how Fluxer handles your microphone. Focused Voice is the recommended default. Direct Input passes the
							raw stream through untouched. Custom gives you the processing controls.
						</Trans>
					</p>
					<RadioGroup
						options={profileOptions}
						value={voiceProcessingMode}
						onChange={handleVoiceProcessingModeChange}
						aria-label={t`Select an input profile`}
					/>
					{activeInputLabel != null && (
						<p className={styles.profileHelperText}>
							{hasDeviceOverride ? (
								<>
									<Trans>
										Saved for <strong>{activeInputLabel}</strong>. Fluxer will reapply it whenever you switch back to
										this input.
									</Trans>{' '}
									<Button variant="secondary" small={true} onClick={handleClearDeviceOverride}>
										<Trans>Use the default profile</Trans>
									</Button>
								</>
							) : (
								<Trans>
									Choosing a profile here saves it for <strong>{activeInputLabel}</strong>, so you don't have to redo it
									next time you switch inputs.
								</Trans>
							)}
						</p>
					)}
					{voiceProcessingMode === 'voice' && <div className={styles.profileSubSection}>{renderPttControls()}</div>}
					{voiceProcessingMode === 'studio' && (
						<>
							<p className={styles.profileHelperText}>
								<Trans>
									Direct Input stays live and ignores Push-to-Talk. Fluxer leaves echo cancellation, noise suppression
									and gain control off so your own chain stays untouched.
								</Trans>
							</p>
							{pttCombo?.key && isPushToTalk && (
								<WarningAlert>
									<Trans>
										You have Push-to-Talk bound, but it's ignored under Direct Input. Switch to Focused Voice or Custom
										to use Push-to-Talk.
									</Trans>
								</WarningAlert>
							)}
						</>
					)}
					{voiceProcessingMode === 'custom' && renderCustomProfile()}
				</div>
			</div>
			<MicTestSection settings={voiceSettings} />
			<EntranceSoundSection />
		</>
	);
});
